/**
 * TanStack Query hooks for the clinician portal.
 *
 * The queue and the open conversation both poll (20s / 15s) because the
 * global QueryClient disables refetchOnWindowFocus — without an interval
 * the inbox would only update on a hard refresh, which is exactly the
 * workflow this portal exists to kill.
 */
import type { ClinicalIntakeEvidence } from "@/types/intakeEvidence";
import type { CheckinDoseHistoryResponse } from "@/types/checkinDoseHistory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "./client";
import type {
  ClinicianAvailability,
  ClinicianAvailabilityRange,
  ClinicianTimeOff,
  PrescriberCoverage,
  DoseRequestDecision,
  ClaimResponse,
  ClinicianGuide,
  ClinicianReportResponse,
  ClinicianReportType,
  ClinicianMessageAutomation,
  ClinicianMessageAutomationDelivery,
  ClinicianMessageAutomationInput,
  ClinicianRulesAutomations,
  ClinicianInboxTagPriority,
  ClinicianSession,
  ClinicalAssignmentRule,
  ClinicalTeam,
  ClinicalTeamMember,
  PatientAssignmentOverview,
  PatientRotationBatch,
  PatientRotationPreview,
  PrescriberIdentityFields,
  PrescriberIdentityResponse,
  AppointmentAttendanceRecord,
  AppointmentPresence,
  ClinicianIntakeTemplate,
  ClinicianDirectionScope,
  ClinicianProduct,
  ClinicianTemplate,
  ClinicianTemplateHistory,
  ConversationHandoffEvent,
  CosignReview,
  CosignSampleResult,
  CosignSummaryRow,
  HandoffTargets,
  SyncVisitStates,
  ClinicianTemplateKind,
  ClinicianWorklistResponse,
  NurseAppointmentsResponse,
  ConversationDetail,
  DraftResponse,
  DraftVariant,
  GuideShare,
  PatientBriefResponse,
  PatientAIResponseControl,
  PatientChatAttachment,
  PatientDirectoryResponse,
  PatientRecordResponse,
  PostVisitReviewResponse,
  PostVisitReviewSubmit,
  PostVisitReviewSubmitResponse,
  QueueResponse,
  ReplyDisposition,
  ReplyResponse,
  PrescriptionStage,
  VisitRequest,
  VisitRequestReason,
} from "@/types/clinician";

const clinicalTeamKey = ["clinician", "team"] as const;
const clinicianSessionKey = ["clinician", "session"] as const;

export function useMasterProductEvidence(patientId: string | undefined) {
  return useQuery({
    queryKey: ["clinician", "patient", patientId, "master-product-intake"],
    queryFn: () =>
      apiFetch<ClinicalIntakeEvidence>(
        `/clinician/patients/${encodeURIComponent(patientId!)}/master-product-intake`,
      ),
    enabled: Boolean(patientId),
    staleTime: 15_000,
    retry: 1,
  });
}
const clinicianRulesAutomationsKey = [
  "clinician",
  "rules-automations",
] as const;

export function useClinicianSession() {
  return useQuery({
    queryKey: clinicianSessionKey,
    queryFn: () => apiFetch<ClinicianSession>("/clinician/session"),
    staleTime: 300_000,
  });
}

export function useNurseAppointments(enabled = true) {
  return useQuery({
    queryKey: ["clinician", "nurse-appointments"],
    queryFn: () =>
      apiFetch<NurseAppointmentsResponse>("/clinician/nurse-appointments"),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useClinicianRulesAutomations() {
  return useQuery({
    queryKey: clinicianRulesAutomationsKey,
    queryFn: () =>
      apiFetch<ClinicianRulesAutomations>("/clinician/rules-automations"),
    staleTime: 60_000,
  });
}

export function useUpdateClinicianAutoSilence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { enabled: boolean; duration_minutes: number }) =>
      apiFetch<ClinicianRulesAutomations["auto_silence"]>(
        "/clinician/rules-automations/auto-silence",
        { method: "PUT", body: JSON.stringify(body) },
      ),
    onSuccess: (autoSilence) => {
      qc.setQueryData<ClinicianRulesAutomations>(
        clinicianRulesAutomationsKey,
        (current) =>
          current ? { ...current, auto_silence: autoSilence } : current,
      );
    },
  });
}

export interface ClinicianInboxTagInput {
  label: string;
  rule: string;
  priority: ClinicianInboxTagPriority;
  enabled: boolean;
}

export function useCreateClinicianInboxTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ClinicianInboxTagInput) =>
      apiFetch<ClinicianRulesAutomations["tags"][number]>(
        "/clinician/rules-automations/tags",
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clinicianRulesAutomationsKey });
      qc.invalidateQueries({ queryKey: queueKey });
    },
  });
}

export function useUpdateClinicianInboxTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ClinicianInboxTagInput & { id: string }) =>
      apiFetch<ClinicianRulesAutomations["tags"][number]>(
        `/clinician/rules-automations/tags/${id}`,
        { method: "PUT", body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clinicianRulesAutomationsKey });
      qc.invalidateQueries({ queryKey: queueKey });
    },
  });
}

const clinicianMessageAutomationsKey = [
  "clinician",
  "rules-automations",
  "messages",
] as const;

export function useClinicianMessageAutomations() {
  return useQuery({
    queryKey: clinicianMessageAutomationsKey,
    queryFn: () =>
      apiFetch<{ items: ClinicianMessageAutomation[] }>(
        "/clinician/rules-automations/messages",
      ),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useCreateClinicianMessageAutomation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ClinicianMessageAutomationInput) =>
      apiFetch<ClinicianMessageAutomation>(
        "/clinician/rules-automations/messages",
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: clinicianMessageAutomationsKey }),
  });
}

export function useUpdateClinicianMessageAutomation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: ClinicianMessageAutomationInput & { id: string }) =>
      apiFetch<ClinicianMessageAutomation>(
        `/clinician/rules-automations/messages/${id}`,
        { method: "PUT", body: JSON.stringify(body) },
      ),
    onSuccess: (_automation, variables) => {
      qc.invalidateQueries({ queryKey: clinicianMessageAutomationsKey });
      qc.invalidateQueries({
        queryKey: [
          "clinician",
          "rules-automations",
          "messages",
          variables.id,
          "deliveries",
        ],
      });
    },
  });
}

export function useArchiveClinicianMessageAutomation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean }>(`/clinician/rules-automations/messages/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: clinicianMessageAutomationsKey }),
  });
}

export function useClinicianMessageAutomationDeliveries(
  id: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["clinician", "rules-automations", "messages", id, "deliveries"],
    queryFn: () =>
      apiFetch<{ items: ClinicianMessageAutomationDelivery[] }>(
        `/clinician/rules-automations/messages/${id}/deliveries`,
      ),
    enabled,
    staleTime: 15_000,
    refetchInterval: enabled ? 15_000 : false,
  });
}

export function useClinicalTeam(enabled = true) {
  return useQuery({
    queryKey: clinicalTeamKey,
    queryFn: () => apiFetch<ClinicalTeam>("/clinician/team"),
    enabled,
    staleTime: 60_000,
  });
}

export function useUpdateClinicalTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      clinicianId,
      ...body
    }: Partial<
      Pick<
        ClinicalTeamMember,
        | "healthie_provider_id"
        | "active"
        | "provider_type"
        | "team_patient_scope"
        | "licensed_states"
        | "credentials"
        | "accepting_new_patients"
        | "timezone"
        | "notification_mode"
        | "notify_clinical_messages"
        | "notify_appointments"
      >
    > & { clinicianId: string }) =>
      apiFetch<ClinicalTeamMember>(
        `/clinician/team/clinicians/${clinicianId}`,
        { method: "PATCH", body: JSON.stringify(body) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: clinicalTeamKey }),
  });
}

export function useSaveClinicalAssignmentRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rules,
      keepExistingPatientsWith,
    }: {
      rules: ClinicalAssignmentRule[];
      /** Who keeps patients with no assignment when routing is turned on. */
      keepExistingPatientsWith?: string | null;
    }) =>
      apiFetch<ClinicalTeam>("/clinician/team/rules", {
        method: "PUT",
        body: JSON.stringify({
          rules,
          keep_existing_patients_with: keepExistingPatientsWith ?? null,
          confirmed: true,
        }),
      }),
    onSuccess: (team) => {
      qc.setQueryData(clinicalTeamKey, team);
      qc.invalidateQueries({ queryKey: worklistKey });
      qc.invalidateQueries({ queryKey: queueKey });
    },
  });
}

const prescriberIdentityKey = (clinicianId: string) =>
  ["clinician", "team", "identity", clinicianId] as const;
const patientAssignmentKey = (patientId: string) =>
  ["clinician", "team", "patient-assignment", patientId] as const;

export function usePrescriberIdentity(clinicianId: string, enabled = true) {
  return useQuery({
    queryKey: prescriberIdentityKey(clinicianId),
    queryFn: () =>
      apiFetch<PrescriberIdentityResponse>(
        `/clinician/team/clinicians/${encodeURIComponent(clinicianId)}/identity`,
      ),
    enabled: Boolean(clinicianId) && enabled,
    staleTime: 60_000,
  });
}

export function useSavePrescriberIdentity(clinicianId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (identity: PrescriberIdentityFields) =>
      apiFetch<PrescriberIdentityResponse>(
        `/clinician/team/clinicians/${encodeURIComponent(clinicianId)}/identity`,
        { method: "PUT", body: JSON.stringify(identity) },
      ),
    onSuccess: (result) => {
      qc.setQueryData(prescriberIdentityKey(clinicianId), result);
      qc.invalidateQueries({ queryKey: clinicalTeamKey });
    },
  });
}

export function useUploadPrescriberSignature(clinicianId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append("file", file);
      return apiFetch<PrescriberIdentityResponse>(
        `/clinician/team/clinicians/${encodeURIComponent(clinicianId)}/identity/signature`,
        { method: "POST", body },
      );
    },
    onSuccess: (result) => {
      qc.setQueryData(prescriberIdentityKey(clinicianId), result);
      qc.invalidateQueries({ queryKey: clinicalTeamKey });
    },
  });
}

export function usePatientAssignment(patientId: string | null, enabled = true) {
  return useQuery({
    queryKey: patientAssignmentKey(patientId ?? ""),
    queryFn: () =>
      apiFetch<PatientAssignmentOverview>(
        `/clinician/team/patients/${encodeURIComponent(patientId!)}/assignment`,
      ),
    enabled: Boolean(patientId) && enabled,
    staleTime: 30_000,
  });
}

const prescriberCoverageKey = ["clinician", "team", "coverage"] as const;

export function useReassignPatient(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clinicianId: string) =>
      apiFetch<{ ok: boolean }>(
        `/clinician/team/patients/${encodeURIComponent(patientId)}`,
        {
          method: "PUT",
          body: JSON.stringify({ clinician_id: clinicianId, confirmed: true }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: patientAssignmentKey(patientId) });
      qc.invalidateQueries({ queryKey: clinicalTeamKey });
      qc.invalidateQueries({ queryKey: worklistKey });
      qc.invalidateQueries({ queryKey: queueKey });
      qc.invalidateQueries({ queryKey: prescriberCoverageKey });
    },
  });
}

/** ``onlyUnlicensed``: just the states the prescriber isn't licensed in. */
export function usePatientRotationPreview(
  clinicianId: string,
  enabled = true,
  onlyUnlicensed = false,
) {
  return useQuery({
    queryKey: ["clinician", "team", "rotation-preview", clinicianId, onlyUnlicensed],
    queryFn: () =>
      apiFetch<PatientRotationPreview>(
        `/clinician/team/clinicians/${encodeURIComponent(clinicianId)}/rotation-preview${onlyUnlicensed ? "?only_unlicensed=true" : ""}`,
      ),
    enabled: Boolean(clinicianId) && enabled,
    staleTime: 30_000,
  });
}


/** Patients the team can't reach: no prescriber, an inactive one, or one not
 *  licensed in their state. Supervisors only; refreshes every few minutes. */
export function usePrescriberCoverage(enabled = true) {
  return useQuery({
    queryKey: prescriberCoverageKey,
    queryFn: () => apiFetch<PrescriberCoverage>("/clinician/team/coverage"),
    enabled,
    staleTime: 60_000,
    refetchInterval: 300_000,
  });
}

export function useAssignUnassignedPatients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{
        assigned: number;
        still_unassigned: number;
        failed: number;
        remaining: number;
      }>(
        "/clinician/team/coverage/assign-unassigned",
        { method: "POST", body: JSON.stringify({ confirmed: true }) },
      ),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: prescriberCoverageKey });
      qc.invalidateQueries({ queryKey: clinicalTeamKey });
    },
  });
}

/** Rotate a prescriber off (all patients), or with ``onlyUnlicensed`` move
 * just their patients in states they aren't licensed in. */
export function useRotatePatients(clinicianId: string, onlyUnlicensed = false) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      limit,
      afterPatientId,
    }: {
      limit?: number;
      afterPatientId?: string | null;
    }) =>
      apiFetch<PatientRotationBatch>(
        `/clinician/team/clinicians/${encodeURIComponent(clinicianId)}/${onlyUnlicensed ? "move-unlicensed-patients" : "rotate-patients"}`,
        {
          method: "POST",
          body: JSON.stringify({
            limit: limit ?? 200,
            after_patient_id: afterPatientId ?? null,
            confirmed: true,
          }),
        },
      ),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: prescriberCoverageKey });
      qc.invalidateQueries({ queryKey: clinicalTeamKey });
      qc.invalidateQueries({
        queryKey: ["clinician", "team", "rotation-preview", clinicianId],
      });
      qc.invalidateQueries({ queryKey: worklistKey });
    },
  });
}

const queueKey = ["clinician", "queue"] as const;
const convoKey = (id: string) => ["clinician", "conversation", id] as const;
const attachmentsKey = (id: string) =>
  ["clinician", "conversation", id, "attachments"] as const;
const draftKey = (id: string, kind: "summary" | "reply") =>
  ["clinician", "draft", id, kind] as const;
const patientAttachmentsKey = (id: string) =>
  ["clinician", "patient", id, "attachments"] as const;
const patientAIResponseControlKey = (id: string) =>
  ["clinician", "patient", id, "ai-response-control"] as const;

export function useClinicianQueue(
  includeAnswered = false,
  clinicianId?: string,
  enabled = true,
) {
  const params = new URLSearchParams();
  if (includeAnswered) params.set("include_answered", "true");
  if (clinicianId) params.set("clinician_id", clinicianId);
  return useQuery({
    queryKey: [...queueKey, includeAnswered, clinicianId ?? "all"],
    queryFn: () =>
      apiFetch<QueueResponse>(
        `/clinician/queue${params.size ? `?${params.toString()}` : ""}`,
      ),
    enabled,
    refetchInterval: 20_000,
  });
}

export function useClinicianConversation(conversationId: string) {
  return useQuery({
    queryKey: convoKey(conversationId),
    queryFn: () =>
      apiFetch<ConversationDetail>(
        `/clinician/conversations/${conversationId}`,
      ),
    refetchInterval: 15_000,
  });
}

export function usePatientChatAttachments(
  conversationId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: attachmentsKey(conversationId),
    queryFn: () =>
      apiFetch<PatientChatAttachment[]>(
        `/clinician/conversations/${conversationId}/attachments`,
      ),
    enabled: Boolean(conversationId) && enabled,
  });
}

export function usePatientAttachments(patientId: string | null | undefined) {
  return useQuery({
    queryKey: patientAttachmentsKey(patientId ?? ""),
    queryFn: () =>
      apiFetch<PatientChatAttachment[]>(
        `/clinician/patients/${patientId}/attachments`,
      ),
    enabled: Boolean(patientId),
    staleTime: 60_000,
  });
}

export function usePatientAIResponseControl(
  patientId: string | null | undefined,
) {
  return useQuery({
    queryKey: patientAIResponseControlKey(patientId ?? ""),
    queryFn: () =>
      apiFetch<PatientAIResponseControl>(
        `/clinician/patients/${patientId}/ai-response-control`,
      ),
    enabled: Boolean(patientId),
    refetchInterval: 30_000,
  });
}

export function useUpdatePatientAIResponseControl(
  patientId: string | null | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      mode: "enabled" | "until" | "indefinite";
      paused_until?: string;
      reason?: string;
    }) =>
      apiFetch<PatientAIResponseControl>(
        `/clinician/patients/${patientId}/ai-response-control`,
        { method: "PUT", body: JSON.stringify(body) },
      ),
    onSuccess: (control) => {
      if (patientId) {
        qc.setQueryData(patientAIResponseControlKey(patientId), control);
      }
      qc.invalidateQueries({ queryKey: queueKey });
    },
  });
}

export interface SyncCounts {
  patients: number;
  threads: number;
  created: number;
  hydrated: number;
  skipped: number;
}

export interface SyncStatus {
  running: boolean;
  started?: boolean | null;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  counts: SyncCounts;
}

/**
 * The sync runs server-side in the background (a Healthie round-trip per
 * patient — holding the request open hit gateway timeouts and made
 * "finished" a lie). POST starts it; this status poll drives the button
 * label and keeps the queue list refreshing while rows land.
 */
export function useSyncStatus() {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ["clinician", "sync-status"],
    queryFn: async () => {
      const s = await apiFetch<SyncStatus>("/clinician/queue/sync/status");
      if (s.running) qc.invalidateQueries({ queryKey: queueKey });
      return s;
    },
    refetchInterval: (query) => (query.state.data?.running ? 2_000 : false),
  });
}

export function useSyncQueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<SyncStatus>("/clinician/queue/sync", { method: "POST" }),
    onSuccess: (s) => {
      qc.setQueryData(["clinician", "sync-status"], s);
      qc.invalidateQueries({ queryKey: queueKey });
    },
  });
}

export function useClaimConversation(conversationId: string) {
  return useMutation({
    mutationFn: () =>
      apiFetch<ClaimResponse>(`/clinician/queue/${conversationId}/claim`, {
        method: "POST",
      }),
  });
}

export function useReleaseConversation(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean }>(`/clinician/queue/${conversationId}/release`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queueKey }),
  });
}

export function useSnoozeConversation(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (until: string | null) =>
      apiFetch<{ ok: boolean }>(`/clinician/queue/${conversationId}/snooze`, {
        method: "POST",
        body: JSON.stringify({ until }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queueKey });
      qc.invalidateQueries({ queryKey: convoKey(conversationId) });
    },
  });
}

export function useClinicianReply(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      content,
      disposition = "resolved",
      guide_id,
    }: {
      content: string;
      disposition?: ReplyDisposition;
      guide_id?: string;
    }) =>
      apiFetch<ReplyResponse>(
        `/clinician/conversations/${conversationId}/reply`,
        {
          method: "POST",
          body: JSON.stringify({ content, disposition, guide_id }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: convoKey(conversationId) });
      qc.invalidateQueries({ queryKey: queueKey });
    },
  });
}

export function useClinicianAttachmentReply(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      content,
      disposition = "resolved",
    }: {
      file: File;
      content: string;
      disposition?: ReplyDisposition;
    }) => {
      const form = new FormData();
      form.append("file", file);
      form.append("body", content);
      form.append("disposition", disposition);
      return apiFetch<ReplyResponse>(
        `/clinician/conversations/${conversationId}/attachments`,
        { method: "POST", body: form },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: convoKey(conversationId) });
      qc.invalidateQueries({ queryKey: attachmentsKey(conversationId) });
      qc.invalidateQueries({ queryKey: queueKey });
    },
  });
}

export function useTransitionConversation(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      state,
      reason,
    }: {
      state: "resolved" | "closed" | "waiting_patient" | "waiting_internal";
      reason?: string;
    }) =>
      apiFetch<{ ok: boolean }>(
        `/clinician/queue/${conversationId}/transition`,
        {
          method: "POST",
          body: JSON.stringify({ state, reason }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queueKey });
      qc.invalidateQueries({ queryKey: convoKey(conversationId) });
    },
  });
}

export function useHandoffConversation(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      target_capability: "nursing" | "prescriber";
      note?: string;
      /** A specific clinician; default is the patient's own. */
      target_clinician_id?: string;
    }) =>
      apiFetch<{
        ok: boolean;
        target_capability: "nursing" | "prescriber";
        target_clinician_id: string;
      }>(`/clinician/queue/${conversationId}/handoff`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queueKey });
      qc.invalidateQueries({ queryKey: convoKey(conversationId) });
      qc.invalidateQueries({
        queryKey: ["clinician", "handoffs", conversationId],
      });
    },
  });
}

/** Who this conversation can be handed to; the patient's own clinician first. */
export function useHandoffTargets(conversationId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["clinician", "handoff-targets", conversationId],
    queryFn: () =>
      apiFetch<HandoffTargets>(
        `/clinician/queue/${conversationId}/handoff-targets`,
      ),
    enabled: Boolean(conversationId) && enabled,
    staleTime: 60_000,
  });
}

export function useConversationHandoffs(conversationId: string) {
  return useQuery({
    queryKey: ["clinician", "handoffs", conversationId],
    queryFn: () =>
      apiFetch<ConversationHandoffEvent[]>(
        `/clinician/queue/${conversationId}/handoffs`,
      ),
    enabled: Boolean(conversationId),
    staleTime: 30_000,
  });
}

/** Summary and reply drafts can auto-load; the backend normally prepares
 * both when a patient message first enters the queue, so this read should
 * be a cache hit by the time a clinician opens the conversation. */
export function useDraft(
  conversationId: string,
  kind: "summary" | "reply",
  { auto = false }: { auto?: boolean } = {},
) {
  return useQuery({
    queryKey: draftKey(conversationId, kind),
    queryFn: () =>
      apiFetch<DraftResponse>(
        `/clinician/conversations/${conversationId}/draft?kind=${kind}`,
      ),
    enabled: auto,
    staleTime: 60_000,
  });
}

/** Explicit regeneration bypasses the per-patient-message cache. A normal
 * query refetch intentionally remains a cheap cache read. */
export function useRefreshDraft(
  conversationId: string,
  kind: "summary" | "reply",
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variant: DraftVariant = "best") =>
      apiFetch<DraftResponse>(
        `/clinician/conversations/${conversationId}/draft?kind=${kind}&refresh=true&style=${variant}`,
      ),
    onSuccess: (draft) => {
      qc.setQueryData(draftKey(conversationId, kind), draft);
    },
  });
}

export function useDraftOutcome() {
  return useMutation({
    mutationFn: ({
      draftId,
      outcome,
      finalText,
    }: {
      draftId: string;
      outcome: "accepted" | "edited" | "rejected";
      finalText?: string;
    }) =>
      apiFetch<{ ok: boolean }>(`/clinician/drafts/${draftId}/outcome`, {
        method: "POST",
        body: JSON.stringify({ outcome, final_text: finalText }),
      }),
  });
}

export function usePatientRecord(patientId: string | null | undefined) {
  return useQuery({
    queryKey: ["clinician", "patient-record", patientId],
    queryFn: () =>
      apiFetch<PatientRecordResponse>(
        `/clinician/patients/${patientId}/record`,
      ),
    enabled: Boolean(patientId),
    staleTime: 300_000,
  });
}

export function usePatientBrief(conversationId: string) {
  return useQuery({
    queryKey: ["clinician", "patient-brief", conversationId],
    queryFn: () =>
      apiFetch<PatientBriefResponse>(
        `/clinician/conversations/${conversationId}/patient-brief`,
      ),
    enabled: Boolean(conversationId),
    staleTime: 300_000,
  });
}

const worklistKey = ["clinician", "worklist"] as const;

export function useClinicianWorklist(
  clinicianId?: string,
  options?: {
    stage?: string;
    search?: string;
    limit?: number;
    offset?: number;
    enabled?: boolean;
  },
) {
  const params = new URLSearchParams();
  if (clinicianId) params.set("clinician_id", clinicianId);
  if (options?.stage) params.set("prescription_stage", options.stage);
  if (options?.search) params.set("prescription_search", options.search);
  params.set("prescription_limit", String(options?.limit ?? 100));
  params.set("prescription_offset", String(options?.offset ?? 0));
  const query = params.toString();
  return useQuery({
    queryKey: [...worklistKey, clinicianId ?? "all", query],
    queryFn: () =>
      apiFetch<ClinicianWorklistResponse>(
        `/clinician/worklist${query ? `?${query}` : ""}`,
      ),
    enabled: options?.enabled ?? true,
    // Filters and pages are projections of the same queue. Keep the last
    // projection visible while the next one resolves instead of replacing the
    // entire clinical workspace with a skeleton.
    placeholderData: (previousData) => previousData,
    refetchInterval: 30_000,
  });
}

export function useClinicianWorklistItem(
  clinicianId: string | undefined,
  prescriptionId: string | null,
  enabled: boolean,
) {
  const params = new URLSearchParams();
  if (clinicianId) params.set("clinician_id", clinicianId);
  if (prescriptionId) params.set("prescription_id", prescriptionId);
  params.set("prescription_limit", "1");
  params.set("prescription_offset", "0");
  const query = params.toString();
  return useQuery({
    queryKey: [...worklistKey, "item", clinicianId ?? "all", prescriptionId],
    queryFn: async () => {
      const response = await apiFetch<ClinicianWorklistResponse>(
        `/clinician/worklist?${query}`,
      );
      return (
        response.prescriptions.find((item) => item.id === prescriptionId) ??
        null
      );
    },
    enabled: enabled && Boolean(prescriptionId),
    staleTime: 30_000,
  });
}

export function useRefreshClinicianWorklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean }>("/clinician/worklist/refresh", {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: worklistKey }),
  });
}

export function useRecordClinicianVisitOverride(patientId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      order_id: string;
      reason_code:
        | "visit_occurred"
        | "external_visit"
        | "tracking_issue"
        | "other";
      occurred_at: string;
      explanation?: string;
      confirmed: true;
    }) =>
      apiFetch<{
        order_id: string;
        reason_code:
          | "visit_occurred"
          | "external_visit"
          | "tracking_issue"
          | "other";
        occurred_at: string;
        explanation: string | null;
        actor: string;
        clinician_id: string | null;
        recorded_at: string;
      }>(`/clinician/patients/${patientId}/visit-override`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (override) => {
      qc.setQueriesData<ClinicianWorklistResponse>(
        { queryKey: worklistKey },
        (current) => {
          if (!current || !Array.isArray(current.prescriptions)) return current;
          return {
            ...current,
            prescriptions: current.prescriptions.map((prescription) =>
              prescription.id === override.order_id
                ? {
                    ...prescription,
                    stage: "ready_to_prescribe" as const,
                    visit_override: override,
                  }
                : prescription,
            ),
          };
        },
      );
      // Keep the selected work item mounted while its approval modal opens.
      // The completed decision refreshes the authoritative queue afterward.
      void qc.invalidateQueries({ queryKey: worklistKey, refetchType: "none" });
    },
  });
}

export interface VisitRequestResponse {
  order_id: string;
  request: VisitRequest;
  stage: PrescriptionStage;
  patient_notified: boolean;
}

function patchWorklistVisitRequest(
  qc: ReturnType<typeof useQueryClient>,
  response: VisitRequestResponse,
) {
  const open = response.request.resolved_at == null;
  qc.setQueriesData<ClinicianWorklistResponse>(
    { queryKey: worklistKey },
    (current) => {
      if (!current || !Array.isArray(current.prescriptions)) return current;
      return {
        ...current,
        prescriptions: current.prescriptions.map((prescription) =>
          prescription.id === response.order_id
            ? {
                ...prescription,
                stage: response.stage,
                visit_request: response.request,
                consult_reason: open
                  ? ("clinician_request" as const)
                  : prescription.requires_consult_after
                    ? ("state_requirement" as const)
                    : null,
                requires_consult_after: open
                  ? response.request.requested_at
                  : response.request.previous_requires_consult_after_iso,
              }
            : prescription,
        ),
      };
    },
  );
  // Keep the selected work item mounted; the authoritative queue refreshes
  // on the next poll or navigation.
  void qc.invalidateQueries({ queryKey: worklistKey, refetchType: "none" });
}

/** Monthly check-ins, dose and stack requests, and the prescriber's answer. */
export function useClinicianCheckinDoseHistory(patientId: string | null) {
  return useQuery({
    queryKey: ["clinician", "checkin-dose-history", patientId],
    queryFn: () =>
      apiFetch<CheckinDoseHistoryResponse>(
        `/clinician/patients/${patientId}/checkin-dose-history`,
      ),
    enabled: Boolean(patientId),
    staleTime: 60_000,
  });
}

/** Ask the patient to book a live visit before prescribing (any state). */
export function useRequestClinicianVisit(patientId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      order_id: string;
      reason_code: VisitRequestReason;
      note?: string;
    }) =>
      apiFetch<VisitRequestResponse>(
        `/clinician/patients/${patientId}/visit-request`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: (response) => {
      patchWorklistVisitRequest(qc, response);
      qc.invalidateQueries({
        queryKey: ["clinician", "checkin-dose-history", patientId],
      });
    },
  });
}

/** Close an open visit request without a visit; the order returns to its prior gate. */
export function useWithdrawClinicianVisit(patientId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { order_id: string; note?: string }) =>
      apiFetch<VisitRequestResponse>(
        `/clinician/patients/${patientId}/visit-request/withdraw`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: (response) => patchWorklistVisitRequest(qc, response),
  });
}

export function useClinicianReport(
  reportType: ClinicianReportType | null,
  startDate: string,
  endDate: string,
  clinicianId?: string,
  enabled = true,
) {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });
  if (clinicianId) params.set("clinician_id", clinicianId);
  return useQuery({
    queryKey: [
      "clinician",
      "reports",
      reportType,
      startDate,
      endDate,
      clinicianId ?? "all",
    ],
    queryFn: () =>
      apiFetch<ClinicianReportResponse>(
        `/clinician/reports/${reportType}?${params.toString()}`,
      ),
    enabled: Boolean(reportType && startDate && endDate) && enabled,
    placeholderData: (previousData) => previousData,
    retry: 1,
    staleTime: 60_000,
  });
}

export function worklistPatientBriefQuery(
  patientId: string,
  orderId: string | null,
) {
  const query = orderId ? `?order_id=${encodeURIComponent(orderId)}` : "";
  return {
    queryKey: ["clinician", "patient-brief-by-id", patientId, orderId] as const,
    queryFn: () =>
      apiFetch<PatientBriefResponse>(
        `/clinician/patients/${patientId}/brief${query}`,
      ),
    staleTime: 300_000,
  };
}

export function useWorklistPatientBrief(
  patientId: string | null,
  orderId: string | null = null,
) {
  return useQuery({
    ...worklistPatientBriefQuery(patientId ?? "", orderId),
    enabled: Boolean(patientId),
  });
}

export function useClinicianPatientSearch(
  search: string,
  activity: "active" | "inactive" | "all" = "active",
) {
  const normalized = search.trim();
  return useQuery({
    queryKey: ["clinician", "patients", normalized, activity],
    queryFn: () =>
      apiFetch<PatientDirectoryResponse>(
        `/clinician/patients?search=${encodeURIComponent(normalized)}&limit=30&activity=${activity}`,
      ),
    enabled: normalized.length >= 2,
    staleTime: 30_000,
  });
}

export function useSendPatientMessage(patientId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      content: string;
      conversation_id?: string | null;
      guide_id?: string;
    }) =>
      apiFetch<{ ok: boolean; conversation_id: string; note_id: string }>(
        `/clinician/patients/${patientId}/messages`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: ["clinician", "patient-brief-by-id", patientId],
      }),
  });
}

const clinicianProductsKey = ["clinician", "products"] as const;

export function useClinicianProducts() {
  return useQuery({
    queryKey: clinicianProductsKey,
    queryFn: () => apiFetch<ClinicianProduct[]>("/clinician/products"),
    staleTime: 300_000,
  });
}

const intakeFormsKey = ["clinician", "intake-forms"] as const;

export function useClinicianIntakeForms() {
  return useQuery({
    queryKey: intakeFormsKey,
    queryFn: () =>
      apiFetch<ClinicianIntakeTemplate[]>("/clinician/intake-forms"),
    staleTime: 60_000,
  });
}

export function useUpdateClinicianIntakeProductLinks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      formId,
      linkedProducts,
    }: {
      formId: string;
      linkedProducts: {
        product_id: string;
        usages: ("Initial" | "Renewal")[];
      }[];
    }) =>
      apiFetch<{ linked_products: ClinicianIntakeTemplate["linked_products"] }>(
        `/clinician/intake-forms/${formId}/product-links`,
        {
          method: "PUT",
          body: JSON.stringify({
            linked_products: linkedProducts,
            confirmed: true,
          }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: intakeFormsKey }),
  });
}

export function useCreateClinicianIntakeForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name }: { name: string }) =>
      apiFetch<ClinicianIntakeTemplate>("/clinician/intake-forms", {
        method: "POST",
        body: JSON.stringify({ name, confirmed: true }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: intakeFormsKey }),
  });
}

export function useUpdateClinicianIntakeHighlight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      formId,
      moduleId,
      importance,
      label,
    }: {
      formId: string;
      moduleId: string;
      importance: "none" | "clinical" | "safety";
      label?: string;
    }) =>
      apiFetch<ClinicianIntakeTemplate["modules"][number]>(
        `/clinician/intake-forms/${formId}/modules/${moduleId}/highlight`,
        {
          method: "PATCH",
          body: JSON.stringify({ importance, label: label?.trim() || null }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: intakeFormsKey }),
  });
}

export function usePatientConversationDraft(
  patientId: string | null,
  conversationId: string | null,
  { enabled = false }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [
      "clinician",
      "patient-conversation-draft",
      patientId,
      conversationId,
    ],
    queryFn: () =>
      apiFetch<DraftResponse>(
        `/clinician/patients/${patientId}/conversations/${conversationId}/draft?kind=reply`,
      ),
    enabled: enabled && Boolean(patientId && conversationId),
    staleTime: 60_000,
  });
}

export function useUpdateClinicianIntakeModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      formId,
      moduleId,
      ...body
    }: {
      formId: string;
      moduleId: string;
      label: string;
      sublabel: string;
      mod_type: string;
      required: boolean;
      options: string;
      index?: number;
      confirmed: true;
    }) =>
      apiFetch<ClinicianIntakeTemplate["modules"][number]>(
        `/clinician/intake-forms/${formId}/modules/${moduleId}`,
        { method: "PATCH", body: JSON.stringify(body) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: intakeFormsKey }),
  });
}

export function useCreateClinicianIntakeModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      formId,
      ...body
    }: {
      formId: string;
      label: string;
      sublabel: string;
      mod_type: string;
      required: boolean;
      options: string;
      index?: number;
      confirmed: true;
    }) =>
      apiFetch<ClinicianIntakeTemplate["modules"][number]>(
        `/clinician/intake-forms/${formId}/modules`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: intakeFormsKey }),
  });
}

const clinicianGuidesKey = ["clinician", "guides"] as const;

export function useClinicianGuides() {
  return useQuery({
    queryKey: clinicianGuidesKey,
    queryFn: () => apiFetch<ClinicianGuide[]>("/clinician/guides"),
    staleTime: 60_000,
  });
}

export function useUploadClinicianGuide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      title,
      description,
      product_ids,
      document_kind = "guide",
      partner_keys = [],
      match_terms = [],
      lot_number,
      coa_scope,
    }: {
      file: File;
      title: string;
      description?: string;
      product_ids: string[];
      document_kind?: "guide" | "coa";
      partner_keys?: string[];
      match_terms?: string[];
      lot_number?: string;
      coa_scope?: "finished_product" | "raw_material";
    }) => {
      const meta = await apiFetch<{
        guide_id: string;
        upload_url: string;
        upload_token: string | null;
        complete_url: string;
      }>("/clinician/guides/upload-url", {
        method: "POST",
        body: JSON.stringify({
          title,
          description,
          product_ids,
          document_kind,
          partner_keys,
          match_terms,
          lot_number,
          coa_scope,
          filename: file.name,
          mime: file.type || "application/pdf",
          size_bytes: file.size,
        }),
      });
      const put = await fetch(meta.upload_url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": "application/pdf",
          "x-upsert": "false",
        },
      });
      if (!put.ok) throw new Error(`PDF upload failed (${put.status})`);
      return apiFetch<ClinicianGuide>(
        `/clinician/guides/${meta.guide_id}/complete`,
        { method: "POST" },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: clinicianGuidesKey }),
  });
}

export function useUpdateClinicianGuide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      title?: string;
      description?: string;
      product_ids?: string[];
      document_kind?: "guide" | "coa";
      partner_keys?: string[];
      match_terms?: string[];
      lot_number?: string | null;
      coa_scope?: "finished_product" | "raw_material" | null;
      active?: boolean;
    }) =>
      apiFetch<ClinicianGuide>(`/clinician/guides/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: clinicianGuidesKey }),
  });
}

export function useGuideShare(token: string) {
  return useQuery({
    queryKey: ["guide-share", token],
    queryFn: () => apiFetch<GuideShare>(`/guide-shares/${token}`),
    enabled: Boolean(token),
    staleTime: 10 * 60_000,
  });
}

const templatesKey = ["clinician", "templates"] as const;
const directionScopesKey = ["clinician", "direction-scopes"] as const;

export function useClinicianDirectionScopes() {
  return useQuery({
    queryKey: directionScopesKey,
    queryFn: () =>
      apiFetch<ClinicianDirectionScope[]>("/clinician/direction-scopes"),
    staleTime: 300_000,
  });
}

export function useClinicianTemplates(kind?: ClinicianTemplateKind) {
  return useQuery({
    queryKey: [...templatesKey, kind ?? "all"],
    queryFn: () =>
      apiFetch<ClinicianTemplate[]>(
        `/clinician/templates${kind ? `?kind=${kind}` : ""}`,
      ),
    staleTime: 300_000,
  });
}

export type ClinicianTemplateInput = Pick<
  ClinicianTemplate,
  "kind" | "name" | "content"
> &
  Partial<
    Pick<
      ClinicianTemplate,
      "product_id" | "partner_key" | "decision" | "sort_order" | "is_default"
    >
  > & {
    /** Mine only (physician notes and patient messages). */
    personal?: boolean;
  };

export function useCreateClinicianTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ClinicianTemplateInput) =>
      apiFetch<ClinicianTemplate>("/clinician/templates", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templatesKey });
      qc.invalidateQueries({ queryKey: ["clinician", "direction-history"] });
    },
  });
}

export function useClinicianDirectionHistory(
  productId: string | null,
  partnerKey: string | null,
  enabled = true,
) {
  const params = new URLSearchParams();
  if (productId) params.set("product_id", productId);
  if (partnerKey) params.set("partner_key", partnerKey);
  return useQuery({
    queryKey: [
      "clinician",
      "direction-history",
      partnerKey ?? "",
      productId ?? "",
    ],
    queryFn: () =>
      apiFetch<ClinicianTemplateHistory[]>(
        `/clinician/templates/direction-history?${params.toString()}`,
      ),
    enabled: Boolean(productId) && enabled,
  });
}

export function useUpdateClinicianTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: Partial<Omit<ClinicianTemplate, "id" | "kind">> & { id: string }) =>
      apiFetch<ClinicianTemplate>(`/clinician/templates/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templatesKey });
      qc.invalidateQueries({ queryKey: ["clinician", "direction-history"] });
      qc.invalidateQueries({
        queryKey: ["clinician", "patient-brief-by-id"],
      });
    },
  });
}

export function useArchiveClinicianTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean }>(`/clinician/templates/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templatesKey });
      qc.invalidateQueries({
        queryKey: ["clinician", "patient-brief-by-id"],
      });
    },
  });
}

export function useCreateMedicationRecord(patientId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      authorized_refills?: number;
      authorization_confirmed?: boolean;
      order_id: string;
      selected_product_id?: string;
      direction_template_id?: string;
      dosage?: string;
      directions?: string;
      start_date?: string;
      message_template_id?: string;
      patient_message?: string;
      confirmed: true;
    }) =>
      apiFetch<{
        id: string;
        name: string;
        already_exists: boolean;
        patient_message_sent: boolean;
        patient_message_error: string | null;
      }>(`/clinician/patients/${patientId}/medications`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: worklistKey });
      qc.invalidateQueries({
        queryKey: ["clinician", "patient-brief-by-id", patientId],
      });
    },
  });
}

export function useCreatePhysicianReview(patientId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      order_id: string;
      decision: "approve" | "reject";
      note: string;
      message_template_id?: string;
      patient_message?: string;
      confirmed: true;
    }) =>
      apiFetch<{
        id: string;
        decision: "approve" | "reject";
        locked: boolean;
        subscription_cancelled: boolean;
        refund_amount_cents: number | null;
        patient_message_sent: boolean;
        patient_message_error: string | null;
      }>(`/clinician/patients/${patientId}/physician-review`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: worklistKey });
      qc.invalidateQueries({
        queryKey: ["clinician", "patient-brief-by-id", patientId],
      });
    },
  });
}

export function useSubmitPrescriptionDecision(patientId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      authorized_refills?: number;
      authorization_confirmed?: boolean;
      order_id: string;
      decision: "approve" | "reject";
      note: string;
      rejection_reason?: string;
      selected_product_id?: string;
      direction_template_id?: string;
      dosage?: string;
      directions?: string;
      start_date?: string;
      message_template_id?: string;
      patient_message?: string;
      dose_request_decision?: DoseRequestDecision;
      confirmed: true;
    }) =>
      apiFetch<{
        decision: "approve" | "reject";
        review: {
          id: string;
          decision: "approve" | "reject";
          locked: boolean;
          subscription_cancelled: boolean;
          refund_amount_cents: number | null;
          patient_message_sent: boolean;
          patient_message_error: string | null;
        };
        medication: {
          id: string;
          name: string;
          already_exists: boolean;
          patient_message_sent: boolean;
          patient_message_error: string | null;
        } | null;
      }>(`/clinician/patients/${patientId}/prescription-decision`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: worklistKey });
      qc.invalidateQueries({
        queryKey: ["clinician", "patient-brief-by-id", patientId],
      });
      qc.invalidateQueries({
        queryKey: ["clinician", "checkin-dose-history", patientId],
      });
    },
  });
}

export function useCreateMissingInfoRequest(patientId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      order_id: string;
      reason_code:
        | "missing_photo_id"
        | "identity_document"
        | "intake_response"
        | "medical_history"
        | "current_medications"
        | "allergy_details"
        | "lab_results"
        | "visit_required"
        | "other";
      details: string;
      confirmed: true;
      /** Programmatic intake checks the clinician saw (label — detail), so
       *  ops gets the specifics on the ticket. ≤12 items, ≤300 chars each. */
      findings?: string[];
      /** Send the patient back to the product intake (portal shows the form
       *  again; they get an email/SMS). Omit for the reason's default: on for
       *  intake-answer reasons, off for ID, labs, visits and other. */
      request_intake_redo?: boolean;
    }) =>
      apiFetch<{
        id: string;
        reason_code: string;
        details: string | null;
        open: boolean;
        intake_redo_requested?: boolean;
      }>(`/clinician/patients/${patientId}/missing-info`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: worklistKey });
      qc.invalidateQueries({
        queryKey: ["clinician", "patient-brief-by-id", patientId],
      });
    },
  });
}

export function useCompleteClinicianTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) =>
      apiFetch<{ ok: boolean }>(`/clinician/tasks/${taskId}/complete`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: worklistKey }),
  });
}

export function useRescheduleClinicianAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      patientId,
      startsAt,
      timezone,
    }: {
      appointmentId: string;
      patientId: string;
      startsAt: string;
      timezone?: string | null;
    }) =>
      apiFetch<{ ok: boolean }>(
        `/clinician/appointments/${appointmentId}/reschedule`,
        {
          method: "POST",
          body: JSON.stringify({
            patient_id: patientId,
            starts_at: startsAt,
            timezone,
            confirmed: true,
          }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: worklistKey }),
  });
}

export function useJoinClinicianAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      patientId,
    }: {
      appointmentId: string;
      patientId: string;
    }) =>
      apiFetch<{ ok: boolean; join_url: string; joined_at: string }>(
        `/clinician/appointments/${appointmentId}/join`,
        {
          method: "POST",
          body: JSON.stringify({ patient_id: patientId }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: worklistKey }),
  });
}

export function useClinicianAppointmentPresence(
  appointmentId: string,
  patientId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["clinician", "appointment-presence", appointmentId, patientId],
    queryFn: () =>
      apiFetch<AppointmentPresence>(
        `/clinician/appointments/${encodeURIComponent(appointmentId)}/presence?patient_id=${encodeURIComponent(patientId)}`,
      ),
    enabled: enabled && Boolean(appointmentId && patientId),
    refetchInterval: 30_000,
    staleTime: 10_000,
    retry: false,
  });
}

export function useRecordClinicianAppointmentAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      patientId,
      attendanceStatus,
      note,
    }: {
      appointmentId: string;
      patientId: string;
      attendanceStatus: "attended" | "no_show";
      note?: string;
    }) =>
      apiFetch<AppointmentAttendanceRecord>(
        `/clinician/appointments/${encodeURIComponent(appointmentId)}/attendance`,
        {
          method: "POST",
          body: JSON.stringify({
            patient_id: patientId,
            attendance_status: attendanceStatus,
            note,
            confirmed: true,
          }),
        },
      ),
    onSuccess: (record) => {
      void qc.invalidateQueries({ queryKey: worklistKey });
      void qc.invalidateQueries({
        queryKey: [
          "clinician",
          "appointment-presence",
          record.appointment_id,
          record.patient_id,
        ],
      });
      void qc.invalidateQueries({ queryKey: ["clinician", "report"] });
    },
  });
}

export function useClinicianPostVisitReview(
  appointmentId: string | null,
  patientId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ["clinician", "post-visit-review", appointmentId, patientId],
    queryFn: () =>
      apiFetch<PostVisitReviewResponse>(
        `/clinician/appointments/${encodeURIComponent(appointmentId ?? "")}/post-visit-review?patient_id=${encodeURIComponent(patientId ?? "")}`,
      ),
    enabled: enabled && Boolean(appointmentId && patientId),
    staleTime: 15_000,
    retry: 1,
  });
}

export function useSubmitClinicianPostVisitReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      body,
    }: {
      appointmentId: string;
      body: PostVisitReviewSubmit;
    }) =>
      apiFetch<PostVisitReviewSubmitResponse>(
        `/clinician/appointments/${encodeURIComponent(appointmentId)}/post-visit-review`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      ),
    onSuccess: (result) => {
      qc.setQueryData(
        [
          "clinician",
          "post-visit-review",
          result.review.appointment_id,
          result.review.patient_id,
        ],
        result,
      );
      void qc.invalidateQueries({ queryKey: worklistKey });
      void qc.invalidateQueries({
        queryKey: [
          "clinician",
          "post-visit-review",
          result.review.appointment_id,
          result.review.patient_id,
        ],
      });
      void qc.invalidateQueries({
        queryKey: [
          "clinician",
          "patient-brief-by-id",
          result.review.patient_id,
        ],
      });
      void qc.invalidateQueries({ queryKey: ["clinician", "report"] });
    },
  });
}

export function useSeedSyntheticCases() {
  const qc = useQueryClient();
  return useMutation<{ seeded: number; removed: number }, Error, number>({
    mutationFn: (count) =>
      apiFetch<{ seeded: number; removed: number }>(
        "/clinician/dev/synthetic",
        {
          method: "POST",
          body: JSON.stringify({ count }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: queueKey }),
  });
}

export function useClearSyntheticCases() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ seeded: number; removed: number }>(
        "/clinician/dev/synthetic",
        {
          method: "DELETE",
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: queueKey }),
  });
}

const syncVisitStatesKey = ["clinician", "sync-visit-states"] as const;

export function useSyncVisitStates(enabled: boolean) {
  return useQuery({
    queryKey: syncVisitStatesKey,
    queryFn: () =>
      apiFetch<SyncVisitStates>("/clinician/team/sync-visit-states"),
    enabled,
    staleTime: 60_000,
  });
}

export function useUpdateSyncVisitStates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { required: string[]; note?: string }) =>
      apiFetch<SyncVisitStates>("/clinician/team/sync-visit-states", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => qc.setQueryData(syncVisitStatesKey, data),
  });
}

export type CosignScope = "mine" | "prescribed" | "all";
export type CosignStatusFilter = "pending" | "cosigned" | "all";
const cosignKey = ["clinician", "cosigns"] as const;

export function useCosignReviews(
  scope: CosignScope,
  status: CosignStatusFilter,
  month?: string,
) {
  const params = new URLSearchParams({ scope, status });
  if (month) params.set("month", month);
  return useQuery({
    queryKey: [...cosignKey, scope, status, month ?? ""],
    queryFn: () =>
      apiFetch<CosignReview[]>(`/clinician/cosigns?${params.toString()}`),
    placeholderData: (previousData) => previousData,
    staleTime: 30_000,
  });
}

export function useCosignSummary(month: string, enabled: boolean) {
  return useQuery({
    queryKey: [...cosignKey, "summary", month],
    queryFn: () =>
      apiFetch<CosignSummaryRow[]>(
        `/clinician/cosigns/summary?month=${encodeURIComponent(month)}`,
      ),
    enabled: Boolean(month) && enabled,
    staleTime: 60_000,
  });
}

export function useCosignChart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reviewId, note }: { reviewId: string; note?: string }) =>
      apiFetch<CosignReview>(`/clinician/cosigns/${reviewId}/cosign`, {
        method: "POST",
        body: JSON.stringify({ note: note || null }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cosignKey });
      qc.invalidateQueries({ queryKey: ["clinician", "patient-brief-by-id"] });
    },
  });
}

export function useReassignCosigner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      reviewId,
      cosignerClinicianId,
    }: {
      reviewId: string;
      cosignerClinicianId: string;
    }) =>
      apiFetch<CosignReview>(`/clinician/cosigns/${reviewId}/cosigner`, {
        method: "PUT",
        body: JSON.stringify({ cosigner_clinician_id: cosignerClinicianId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: cosignKey }),
  });
}

export function useDrawCosignSample() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { month: string; prescriber_clinician_id?: string }) =>
      apiFetch<CosignSampleResult>("/clinician/cosigns/sample", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: cosignKey }),
  });
}

const availabilityKey = ["clinician", "availability"] as const;

function availabilityScope(clinicianId?: string | null) {
  return clinicianId ? `?clinician_id=${encodeURIComponent(clinicianId)}` : "";
}

/** A clinician's sync-visit hours and time off, read from their Healthie
 *  calendar. ``clinicianId`` (supervisors) manages someone else's; omit it
 *  for the signed-in clinician. */
export function useClinicianAvailability(clinicianId?: string | null, enabled = true) {
  return useQuery({
    queryKey: [...availabilityKey, clinicianId || "me"],
    queryFn: () =>
      apiFetch<ClinicianAvailability>(
        `/clinician/availability${availabilityScope(clinicianId)}`,
      ),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Whether the Availability page is switched on for this user. ``flag`` is the
 * clinician session's ``clinician_availability_editor_enabled``. Only when the
 * session doesn't report it does the page's own read decide: it answers 404
 * (availability_editor_disabled) while the editor is off, and any other answer,
 * such as a supervisor with no calendar of their own, still shows the page.
 * ``ready`` holds that read back until the session has loaded.
 */
export function useAvailabilityEditorEnabled(
  eligible: boolean,
  flag?: boolean,
  ready = true,
): boolean {
  const probe = useQuery({
    queryKey: [...availabilityKey, "me"],
    queryFn: () => apiFetch<ClinicianAvailability>("/clinician/availability"),
    enabled: eligible && ready && flag === undefined,
    staleTime: 600_000,
    retry: false,
  });
  if (!eligible) return false;
  if (flag !== undefined) return flag;
  if (probe.isSuccess) return true;
  const error = probe.error as { status?: number; code?: string } | null;
  if (!error) return false;
  return error.status !== 404 && error.code !== "availability_editor_disabled";
}

export function useSaveClinicianAvailability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      clinicianId,
      timezone,
      weekly,
    }: {
      clinicianId?: string | null;
      timezone: string;
      weekly: ClinicianAvailabilityRange[];
    }) =>
      apiFetch<ClinicianAvailability>("/clinician/availability", {
        method: "PUT",
        body: JSON.stringify({
          ...(clinicianId ? { clinician_id: clinicianId } : {}),
          timezone,
          weekly: weekly.map(({ day_of_week, start, end }) => ({ day_of_week, start, end })),
        }),
      }),
    onSuccess: (saved, { clinicianId }) => {
      qc.setQueryData([...availabilityKey, clinicianId || "me"], saved);
    },
    onError: (error, { clinicianId }) => {
      // A partial Healthie write leaves the calendar between states. The 502
      // carries what Healthie holds now; show that rather than the edited form.
      const key = [...availabilityKey, clinicianId || "me"];
      const current = (error as { code?: string; payload?: Record<string, unknown> })
        .payload?.availability as ClinicianAvailability | undefined;
      if ((error as { code?: string }).code === "availability_write_failed" && current) {
        qc.setQueryData(key, current);
      } else {
        qc.invalidateQueries({ queryKey: key });
      }
    },
  });
}

export function useAddTimeOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      clinicianId,
      start,
      end,
      note,
    }: {
      clinicianId?: string | null;
      start: string;
      end: string;
      note?: string | null;
    }) =>
      apiFetch<ClinicianTimeOff>("/clinician/availability/time-off", {
        method: "POST",
        body: JSON.stringify({
          ...(clinicianId ? { clinician_id: clinicianId } : {}),
          start,
          end,
          ...(note ? { note } : {}),
        }),
      }),
    onSettled: (_data, _error, { clinicianId }) => {
      qc.invalidateQueries({ queryKey: [...availabilityKey, clinicianId || "me"] });
    },
  });
}

export function useRemoveTimeOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clinicianId, id }: { clinicianId?: string | null; id: string }) =>
      apiFetch<void>(
        `/clinician/availability/time-off/${encodeURIComponent(id)}${availabilityScope(clinicianId)}`,
        { method: "DELETE" },
      ),
    onSettled: (_data, _error, { clinicianId }) => {
      qc.invalidateQueries({ queryKey: [...availabilityKey, clinicianId || "me"] });
    },
  });
}
